import { Component, Input, OnInit, OnChanges, SimpleChanges } from '@angular/core';

interface ChatMessage {
  id: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  timestamp: number;
  formattedDate?: string;
}

@Component({
  selector: 'hour4u-admin-whatsapp-chat-history',
  templateUrl: './whatsapp-chat-history.component.html',
  styleUrls: ['./whatsapp-chat-history.component.scss']
})
export class WhatsAppChatHistoryComponent implements OnInit, OnChanges {
  @Input() whatsappChatHistory: string | any;
  chatMessages: ChatMessage[] = [];
  chatTitle: string = '';

  constructor() { }

  ngOnInit() {
    this.parseChatHistory();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['whatsappChatHistory'] && !changes['whatsappChatHistory'].firstChange) {
      this.parseChatHistory();
    }
  }

  parseChatHistory() {
    this.chatMessages = [];
    this.chatTitle = '';

    if (!this.whatsappChatHistory) {
      return;
    }

    try {
      // Parse JSON string if it's a string
      let chatData: any;
      if (typeof this.whatsappChatHistory === 'string') {
        chatData = JSON.parse(this.whatsappChatHistory);
      } else {
        chatData = this.whatsappChatHistory;
      }

      // Extract chat title
      if (chatData.chat && chatData.chat.title) {
        this.chatTitle = chatData.chat.title;
      } else if (chatData.title) {
        this.chatTitle = chatData.title;
      }

      // Extract messages from history
      const messagesObj = (chatData && chatData.chat && chatData.chat.history && chatData.chat.history.messages) || 
                          (chatData && chatData.history && chatData.history.messages) || 
                          {};
      
      if (Object.keys(messagesObj).length === 0) {
        return;
      }

      // Convert messages object to array and sort by timestamp
      const messagesArray: ChatMessage[] = Object.values(messagesObj).map((msg: any) => ({
        id: msg.id,
        role: msg.role,
        content: msg.content || '',
        timestamp: msg.timestamp || 0,
        formattedDate: this.formatDate(msg.timestamp)
      }));

      // Filter out system messages and sort by timestamp
      this.chatMessages = messagesArray
        .filter(msg => msg.role !== 'system') // Exclude system messages from display
        .sort((a, b) => a.timestamp - b.timestamp); // Sort chronologically

    } catch (error) {
      console.error('Error parsing WhatsApp chat history:', error);
      this.chatMessages = [];
    }
  }

  formatDate(timestamp: number): string {
    if (!timestamp) return '';

    const date = new Date(timestamp * 1000);
    const options: Intl.DateTimeFormatOptions = {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    };

    return date.toLocaleDateString('en-US', options);
  }

  formatTime(timestamp: number): string {
    if (!timestamp) return '';

    const date = new Date(timestamp * 1000);
    const hours = date.getHours();
    const minutes = date.getMinutes();
    const ampm = hours >= 12 ? 'PM' : 'AM';
    const displayHours = hours % 12 || 12;
    const displayMinutes = minutes.toString().padStart(2, '0');

    return `${displayHours}:${displayMinutes} ${ampm}`;
  }

  isUserMessage(role: string): boolean {
    return role === 'user';
  }

  isAssistantMessage(role: string): boolean {
    return role === 'assistant';
  }

  shouldShowDateSeparator(index: number): boolean {
    if (index === 0) return true;
    
    const currentMessage = this.chatMessages[index];
    const previousMessage = this.chatMessages[index - 1];
    
    if (!currentMessage || !previousMessage) return false;
    
    const currentDate = new Date(currentMessage.timestamp * 1000).toDateString();
    const previousDate = new Date(previousMessage.timestamp * 1000).toDateString();
    
    return currentDate !== previousDate;
  }

  getDateLabel(timestamp: number): string {
    if (!timestamp) return '';
    
    const date = new Date(timestamp * 1000);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    
    const dateStr = date.toDateString();
    const todayStr = today.toDateString();
    const yesterdayStr = yesterday.toDateString();
    
    if (dateStr === todayStr) {
      return 'Today';
    } else if (dateStr === yesterdayStr) {
      return 'Yesterday';
    } else {
      const options: Intl.DateTimeFormatOptions = {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      };
      return date.toLocaleDateString('en-US', options);
    }
  }

  formatMessageContent(content: string): string {
    if (!content) return '';
    
    // Convert markdown-style formatting to HTML
    let formatted = content
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>') // Bold
      .replace(/\*(.*?)\*/g, '<em>$1</em>') // Italic
      .replace(/\n/g, '<br>'); // Line breaks
    
    return formatted;
  }
}

